// Dashboard page component
import React, { Fragment, useState, useEffect } from 'react';
import { useColorModeValue } from '../hooks/useColorModeValue';
import {
  Box,
  Container,
  Heading,
  Grid,
  GridItem,
  VStack,
  HStack,
  Text,
  Button,
  Spinner,
  Badge,
  Icon,
  List,
  Card,
  Progress,
  Alert,
  Table,
  Collapsible,
  IconButton,
} from '@chakra-ui/react';
import { 
  FiShield, 
  FiTrendingUp, 
  FiFileText,
  FiChevronDown,
  FiChevronRight,
  FiPlus,
  FiAlertCircle,
  FiArrowUpRight,
  FiCalendar,
  FiTarget,
  FiAward,
  FiEye,
  FiDownload,
} from 'react-icons/fi';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useDashboardStats, useSystemStandards, useDownloadReviewReport } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { formatCompactDateTime } from '../utils/dateUtils';
import { useTranslation } from 'react-i18next';

import api from '../services/api';
import { toaster } from '../components/ui/toaster';

interface SubscriptionPlan {
  id: number;
  name: string;
  description: string;
  monthly_price: number;
  annual_price: number;
  reviews_per_month: number;
  max_document_size_mb: number;
  max_documents_per_review: number;
  report_storage_days: number;
  unlimited_standards: boolean;
  features: Record<string, any>;
  is_active: boolean;
}

interface UsageStats {
  current_month: {
    reviews: number;
    policies: number;
  };
  total: {
    reviews: number;
  };
}

// Modern Stat Card with animations - Fixed height for consistency
const ModernStatCard: React.FC<{
  label: string;
  value: number | string;
  icon: any;
  color: string;
  trend?: {
    value: number;
    isUp: boolean;
  };
  subtitle?: string;
  onClick?: () => void;
  usageInfo?: {
    used: number;
    total: number;
    percentage: number;
  };
}> = ({ label, value, icon, color, trend, subtitle, onClick, usageInfo }) => {
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const { t } = useTranslation();
  
  return (
    <Card.Root 
      bg={bg} 
      shadow="sm" 
      border="1px" 
      borderColor={borderColor}
      borderRadius="xl"
      cursor={onClick ? 'pointer' : 'default'}
      transition="all 0.3s ease"
      h="180px" // Fixed height for consistency
      _hover={onClick ? {
        transform: 'translateY(-2px)',
        shadow: 'lg',
        borderColor: `${color}.200`,
      } : {}}
      onClick={onClick}
    >
      <Card.Body p={6} h="100%">
        <VStack align="start" gap={3} h="100%" justify="space-between">
          <HStack justify="space-between" align="start" w="100%">
            <VStack align="start" gap={2}>
              <Text color="gray.500" fontSize="sm" fontWeight="medium" textTransform="uppercase" letterSpacing="wide">
                {label}
              </Text>
              <Text fontSize="3xl" fontWeight="bold" color={color} lineHeight="1">
                {value}
              </Text>
                {subtitle && (
                  <Text fontSize="sm" color="gray.400" fontWeight="medium">
                    {subtitle}
                  </Text>
                )}
              </VStack>
              <Box 
                p={3} 
                rounded="xl" 
                bg={`${color}.50`}
                color={`${color}.600`}
                transition="all 0.3s ease"
                _groupHover={{
                  transform: 'scale(1.1)',
                  bg: `${color}.100`,
                }}
              >
                <Icon as={icon} boxSize={6} />
              </Box>
            </HStack>
            
            <VStack gap={2} align="stretch" w="100%">
              {usageInfo && (
                <VStack gap={2} align="stretch">
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.600">
                      {t('dashboard.usageValue', { used: usageInfo.used, total: usageInfo.total === -1 ? '∞' : usageInfo.total })}
                    </Text>
                    <Text fontSize="sm" fontWeight="medium" color={color}>
                      {usageInfo.percentage}%
                    </Text>
                  </HStack>
                  <Progress.Root value={usageInfo.percentage} size="sm" colorPalette={color}>
                    <Progress.Track>
                      <Progress.Range borderRadius="full" />
                    </Progress.Track>
                  </Progress.Root>
                </VStack>
              )}
              
              {trend && (
                <HStack gap={1} fontSize="sm">
                  <Icon as={trend.isUp ? FiArrowUpRight : FiTrendingUp} color={trend.isUp ? 'green.500' : 'red.500'} />
                  <Text color={trend.isUp ? 'green.500' : 'red.500'} fontWeight="medium">
                    {t('dashboard.trendFromLastMonth', { value: Math.abs(trend.value) })}
                  </Text>
                </HStack>
              )}
            </VStack>
          </VStack>
      </Card.Body>
    </Card.Root>
  );
};

// Compact Standard List Item
const getStandardIcon = (standardType: string): string => {
  const t = (standardType || '').toUpperCase();
  if (t.includes('ISO')) return '🔒';
  if (t.includes('SOC')) return '🏢';
  if (t.includes('NIST')) return '🛡️';
  if (t.includes('GDPR')) return '📋';
  if (t.includes('PCI')) return '💳';
  if (t.includes('DGA')) return '🏛️';
  return '📄';
};

const getStandardColor = (standardType: string): string => {
  const t = (standardType || '').toUpperCase();
  if (t.includes('ISO')) return 'blue';
  if (t.includes('SOC')) return 'green';
  if (t.includes('NIST')) return 'purple';
  if (t.includes('GDPR')) return 'orange';
  if (t.includes('PCI')) return 'teal';
  if (t.includes('DGA')) return 'cyan';
  return 'gray';
};

// Presentation colour for the admin-set classification (Standard/Framework/Regulation)
const getClassificationColor = (classification?: string): string => {
  switch ((classification || '').toLowerCase()) {
    case 'standard':
      return 'blue';
    case 'framework':
      return 'purple';
    case 'regulation':
      return 'orange';
    default:
      return 'gray';
  }
};

const StandardListItem: React.FC<{ standard: any }> = ({ standard }) => {
  const navigate = useNavigate();
  const itemBg = useColorModeValue('white', 'gray.800');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const borderCol = useColorModeValue('gray.200', 'gray.700');
  const titleColor = useColorModeValue('gray.800', 'gray.100');
  const color = getStandardColor(standard.standard_type);
  const count = standard.total_sections ?? standard.sections_data?.sections?.length ?? 0;
  const { t } = useTranslation();
  const getClassificationLabel = (classification: string) => {
    switch (classification.toLowerCase()) {
      case 'standard': return t('dashboard.classificationStandard');
      case 'framework': return t('dashboard.classificationFramework');
      case 'regulation': return t('dashboard.classificationRegulation');
      default: return classification;
    }
  };

  return (
    <List.Item listStyleType="none">
      <Box
        as="button"
        w="full"
        textAlign="left"
        onClick={() => navigate(`/standards?standard=${standard.id}`)}
        borderWidth="1px"
        borderColor={borderCol}
        borderRadius="lg"
        bg={itemBg}
        p={3}
        mb={2}
        transition="all 0.15s"
        _hover={{ bg: hoverBg, borderColor: `${color}.300` }}
      >
        <HStack justify="space-between" align="center">
          <HStack gap={3} align="center" minW={0}>
            <Box fontSize="lg" lineHeight="1.1">
              {getStandardIcon(standard.standard_type)}
            </Box>
            <VStack align="start" gap={0.5} minW={0}>
              <Text fontSize="sm" fontWeight="semibold" color={titleColor} lineClamp={1}>
                {standard.name}
              </Text>
              <HStack gap={1.5}>
                {standard.classification && (
                  <Badge
                    colorPalette={getClassificationColor(standard.classification)}
                    variant="solid"
                    fontSize="10px"
                    borderRadius="full"
                  >
                    {getClassificationLabel(standard.classification)}
                  </Badge>
                )}
                <Badge colorPalette={color} variant="subtle" fontSize="10px" borderRadius="full">
                  {standard.standard_type}
                </Badge>
                <Text fontSize="11px" color="gray.400">
                  {t('dashboard.controlsCount', { count })}
                </Text>
                {!standard.is_active && (
                  <Badge colorPalette="red" variant="outline" fontSize="10px">
                    {t('dashboard.inactive')}
                  </Badge>
                )}
              </HStack>
            </VStack>
          </HStack>
          <Icon as={FiChevronRight} color="gray.400" flexShrink={0} />
        </HStack>
      </Box>
    </List.Item>
  );
};

const Dashboard: React.FC = () => {
  const { data: stats, isLoading, error } = useDashboardStats();
  const { data: systemStandards, isLoading: standardsLoading } = useSystemStandards();
  const { user } = useAuth();
  const [subscriptionPlan, setSubscriptionPlan] = useState<SubscriptionPlan | null>(null);
  const [usageStats, setUsageStats] = useState<UsageStats | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const downloadMutation = useDownloadReviewReport();
  const bg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const { t } = useTranslation();

  // Helper functions for reviews table
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'green';
      case 'processing': return 'blue';
      case 'failed': return 'red';
      default: return 'gray';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'green';
    if (score >= 60) return 'yellow';
    return 'red';
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed': return t('dashboard.statusCompleted');
      case 'processing': return t('dashboard.statusProcessing');
      case 'failed': return t('dashboard.statusFailed');
      default: return status.toUpperCase();
    }
  };

  const getComplianceLevel = (score: number) => {
    if (score >= 80) return t('dashboard.levelExcellent');
    if (score >= 60) return t('dashboard.levelGood');
    if (score >= 40) return t('dashboard.levelFair');
    return t('dashboard.levelPoor');
  };

  const toggleRowExpansion = (reviewId: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(reviewId)) {
      newExpanded.delete(reviewId);
    } else {
      newExpanded.add(reviewId);
    }
    setExpandedRows(newExpanded);
  };

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        // Fetch user's subscription plan
        const planResponse = await api.get('/api/user/subscription-plan');
        setSubscriptionPlan(planResponse.data);
        
        // Fetch usage stats
        const usageResponse = await api.get('/api/user/usage/current');
        setUsageStats({
          current_month: {
            reviews: usageResponse.data.current_month.reviews.used,
            policies: usageResponse.data.current_month.policies.used
          },
          total: {
            reviews: usageResponse.data.current_month.reviews.used
          }
        });
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
      }
    };

    if (user) {
      loadDashboardData();
    }
  }, [user]);

  const getUsagePercentage = () => {
    if (!subscriptionPlan || !usageStats) return 0;
    if (subscriptionPlan.reviews_per_month === -1) return 0; // Unlimited
    return Math.round(Math.min((usageStats.current_month.reviews / subscriptionPlan.reviews_per_month) * 100, 100));
  };

  // Use server-computed stats from ALL reviews (not just recent 5)
  const successRate = stats?.success_rate ?? 0;
  const avgScore = stats?.avg_compliance_score ?? 0;

  const handleDownload = async (review: any) => {
    try {
      const blob = await downloadMutation.mutateAsync(review.id.toString());
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename
      const policyName = review.policy_name?.replace(/[^a-zA-Z0-9]/g, '_') || 'policy';
      const standardName = review.standard_name?.replace(/[^a-zA-Z0-9]/g, '_') || 'standard';
      link.download = `NEUREV_Assessment_${policyName}_${standardName}_${review.id}.docx`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toaster.create({
        title: t('dashboard.reportDownloadedSuccessfully'),
        type: 'success',
        duration: 3000,
      });
    } catch (error: any) {
      toaster.create({
        title: t('dashboard.downloadFailed'),
        description: error.response?.data?.message || t('dashboard.anErrorOccurred'),
        type: 'error',
        duration: 5000,
      });
    }
  };

  if (isLoading) {
    return (
      <Box bg={bg} minH="100vh" py={8}>
        <Container maxW="7xl">
          <VStack justify="center" align="center" h="60vh" gap={6}>
            <Box position="relative">
              <Spinner size="xl" color="blue.500" />
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                bg="white"
                borderRadius="full"
                p={2}
              >
                <Icon as={FiShield} boxSize={6} color="blue.500" />
              </Box>
            </Box>
            <VStack gap={2}>
              <Text fontSize="lg" fontWeight="medium" color="gray.600">
                {t('dashboard.loadingDashboard')}
              </Text>
              <Text fontSize="sm" color="gray.400">
                {t('dashboard.preparingOverview')}
              </Text>
            </VStack>
          </VStack>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box bg={bg} minH="100vh" py={8}>
        <Container maxW="7xl">
          <Alert.Root status="error" borderRadius="xl">
            <Alert.Indicator>
              <Icon as={FiAlertCircle} />
            </Alert.Indicator>
            <Box>
              <Alert.Title>{t('dashboard.failedToLoadDashboardData')}</Alert.Title>
              <Alert.Description>{t('dashboard.refreshOrTryAgain')}</Alert.Description>
            </Box>
          </Alert.Root>
        </Container>
      </Box>
    );
  }

  return (
    <Box bg={bg} minH="100vh" py={8}>
      <Container maxW="7xl">
        <VStack gap={4} align="stretch">
          {/* Key Metrics - All cards same size */}
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={6}>
              <GridItem>
                <ModernStatCard
                  label={t('dashboard.totalAssessments')}
                  value={stats?.reviews_count || 0}
                  icon={FiFileText}
                  color="blue"
                  subtitle={t('dashboard.allTime')}
                />
              </GridItem>
              <GridItem>
                <ModernStatCard
                  label={t('dashboard.thisMonth')}
                  value={usageStats?.current_month.reviews || 0}
                  icon={FiCalendar}
                  color="green"
                  subtitle={t('dashboard.reviewsCompleted')}
                  usageInfo={{
                    used: usageStats?.current_month.reviews || 0,
                    total: subscriptionPlan?.reviews_per_month || 3,
                    percentage: getUsagePercentage()
                  }}
                />
              </GridItem>
              <GridItem>
                <ModernStatCard
                  label={t('dashboard.successRate')}
                  value={`${successRate.toFixed(1)}%`}
                  icon={FiTarget}
                  color="purple"
                  subtitle={t('dashboard.allTime')}
                />
              </GridItem>
              <GridItem>
                <ModernStatCard
                  label={t('dashboard.avgScore')}
                  value={`${avgScore.toFixed(1)}%`}
                  icon={FiAward}
                  color="orange"
                  subtitle={t('dashboard.allCompletedReviews')}
                />
              </GridItem>
            </Grid>

          {/* Available Compliance Frameworks */}
          <Card.Root bg={cardBg} shadow="sm" borderRadius="xl" border="1px" borderColor={useColorModeValue('gray.100', 'gray.700')} h="fit-content">
            <Card.Body p={6}>
              <VStack align="stretch" gap={4}>
                <HStack justify="space-between">
                  <VStack align="start" gap={1}>
                    <Heading size="md" fontWeight="bold">{t('dashboard.complianceFrameworks')}</Heading>
                    <Text fontSize="sm" color="gray.500">
                      {t('dashboard.frameworksYouCanAssessAgainst')}
                    </Text>
                  </VStack>
                  <Badge colorScheme="blue" variant="subtle" borderRadius="full" px={3} py={1}>
                    {systemStandards?.length || 0}
                  </Badge>
                </HStack>
                
                {standardsLoading ? (
                  <HStack justify="center" py={6} gap={3}>
                    <Spinner size="md" color="blue.500" />
                    <Text fontSize="sm" color="gray.500">{t('dashboard.loadingFrameworks')}</Text>
                  </HStack>
                ) : systemStandards && systemStandards.length > 0 ? (
                  <VStack align="stretch" gap={3}>
                    <Box
                      maxH="360px"
                      overflowY="auto"
                      pr={2}
                      css={{
                        '&::-webkit-scrollbar': { width: '8px' },
                        '&::-webkit-scrollbar-track': { background: 'transparent' },
                        '&::-webkit-scrollbar-thumb': {
                          background: 'var(--chakra-colors-gray-300)',
                          borderRadius: '8px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                          background: 'var(--chakra-colors-gray-400)',
                        },
                      }}
                    >
                      <List.Root gap={0}>
                        {systemStandards.slice(0, 6).map((standard: any) => (
                          <StandardListItem key={standard.id} standard={standard} />
                        ))}
                      </List.Root>
                    </Box>
                    <RouterLink to="/standards" style={{ textDecoration: 'none' }}>
                      <Button variant="outline" size="sm" colorScheme="blue" w="full">
                        <Icon as={FiArrowUpRight} /> {t('dashboard.browseAllClauses')}
                      </Button>
                    </RouterLink>
                  </VStack>
                ) : (
                  <VStack gap={3} py={6}>
                    <Box 
                      p={3} 
                      rounded="full" 
                      bg="gray.100"
                      color="gray.400"
                    >
                      <Icon as={FiShield} boxSize={6} />
                    </Box>
                    <VStack gap={1}>
                      <Text color="gray.500" textAlign="center" fontWeight="medium" fontSize="sm">
                        {t('dashboard.noFrameworksAvailable')}
                      </Text>
                      <Text fontSize="xs" color="gray.400" textAlign="center">
                        {t('dashboard.contactAdministrator')}
                      </Text>
                    </VStack>
                  </VStack>
                )}
              </VStack>
            </Card.Body>
          </Card.Root>

          {/* Recent Reviews - Same as ReviewsPage */}
          <Card.Root bg={cardBg} shadow="sm" borderRadius="xl" border="1px" borderColor={useColorModeValue('gray.100', 'gray.700')} h="fit-content">
            <Card.Body p={6}>
              <VStack align="stretch" gap={6}>
                <HStack justify="space-between">
                  <VStack align="start" gap={1}>
                    <Heading size="md" fontWeight="bold">{t('dashboard.recentAssessments')}</Heading>
                    <Text fontSize="sm" color="gray.500">
                      {t('dashboard.latestComplianceAssessments')}
                    </Text>
                  </VStack>
                  <RouterLink to="/reviews" style={{ textDecoration: 'none' }}>
                    <Button
                      size="sm"
                      variant="ghost"
                      colorPalette="blue"
                      borderRadius="full"
                    >
                      {t('dashboard.viewAll')}
                      <FiArrowUpRight />
                    </Button>
                  </RouterLink>
                </HStack>
                
                {stats?.recent_reviews && stats.recent_reviews.length > 0 ? (
                  <Box overflowX="auto">
                    <Table.Root variant="line" size="sm">
                      <Table.Header>
                        <Table.Row>
                          <Table.ColumnHeader fontSize="xs" w="25%">{t('dashboard.assessment')}</Table.ColumnHeader>
                          <Table.ColumnHeader fontSize="xs" w="10%">{t('dashboard.status')}</Table.ColumnHeader>
                          <Table.ColumnHeader fontSize="xs" w="12%">{t('dashboard.score')}</Table.ColumnHeader>
                          <Table.ColumnHeader fontSize="xs" w="20%">{t('dashboard.document')}</Table.ColumnHeader>
                          <Table.ColumnHeader fontSize="xs" w="20%">{t('dashboard.standard')}</Table.ColumnHeader>
                          <Table.ColumnHeader fontSize="xs" w="8%">{t('dashboard.created')}</Table.ColumnHeader>
                          <Table.ColumnHeader fontSize="xs" w="5%">{t('dashboard.actions')}</Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {stats.recent_reviews.slice(0, 5).map((review) => {
                          const isExpanded = expandedRows.has(review.id);
                          return (
                            <Fragment key={review.id}>
                              <Table.Row _hover={{ bg: 'gray.50' }}>
                                <Table.Cell>
                                  <HStack gap={2}>
                                    <IconButton
                                      aria-label={t('dashboard.expandRow')}
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => toggleRowExpansion(review.id)}
                                    >
                                      {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
                                    </IconButton>
                                    <VStack align="start" gap={1}>
                                      <Text fontSize="sm" fontWeight="semibold" color="blue.500">
                                        {review.name || t('dashboard.assessmentNumber', { id: review.id })}
                                      </Text>
                                    </VStack>
                                  </HStack>
                                </Table.Cell>
                                <Table.Cell>
                                  <Badge colorScheme={getStatusColor(review.status)} size="sm">
                                    {getStatusLabel(review.status)}
                                  </Badge>
                                </Table.Cell>
                                <Table.Cell>
                                  {review.compliance_score !== null && review.compliance_score !== undefined ? (
                                    <HStack gap={2}>
                                      <Text fontSize="sm" fontWeight="semibold" color={`${getScoreColor(review.compliance_score)}.500`}>
                                        {review.compliance_score.toFixed(1)}%
                                      </Text>
                                      <Progress.Root value={review.compliance_score} size="sm" w="40px" colorPalette={getScoreColor(review.compliance_score)}>
                                        <Progress.Track borderRadius="full">
                                          <Progress.Range />
                                        </Progress.Track>
                                      </Progress.Root>
                                    </HStack>
                                  ) : (
                                    <Text fontSize="sm" color="gray.500">-</Text>
                                  )}
                                </Table.Cell>
                                <Table.Cell>
                                  <Text fontSize="sm" lineClamp={2}>
                                    {review.policy_name || t('dashboard.unknownDocument')}
                                  </Text>
                                </Table.Cell>
                                <Table.Cell>
                                  <Text fontSize="sm" lineClamp={2}>
                                    {review.standard_name || t('dashboard.unknownStandard')}
                                  </Text>
                                </Table.Cell>
                                <Table.Cell>
                                  <Text fontSize="sm">
                                    {formatCompactDateTime(review.created_at)}
                                  </Text>
                                </Table.Cell>
                                <Table.Cell>
                                  <HStack gap={1}>
                                    {review.status === 'completed' && (
                                      <IconButton
                                        aria-label={t('dashboard.downloadReport')}
                                        title={t('dashboard.downloadReport')}
                                        size="sm"
                                        variant="ghost"
                                        colorPalette="blue"
                                        onClick={() => handleDownload(review)}
                                        disabled={downloadMutation.isLoading}
                                      >
                                        <FiDownload />
                                      </IconButton>
                                    )}
                                    <IconButton
                                      aria-label={t('dashboard.viewDetails')}
                                      title={t('dashboard.viewDetails')}
                                      size="sm"
                                      variant="ghost"
                                      colorPalette="gray"
                                      onClick={() => toggleRowExpansion(review.id)}
                                    >
                                      <FiEye />
                                    </IconButton>
                                  </HStack>
                                </Table.Cell>
                              </Table.Row>
                              <Table.Row>
                                <Table.Cell colSpan={7} p={0}>
                                  <Collapsible.Root open={isExpanded}>
                                    <Collapsible.Content>
                                      <Box bg="gray.50" p={3} borderTop="1px" borderColor="gray.200">
                                        <VStack align="stretch" gap={2}>
                                          {/* Additional Info in compact format */}
                                          <HStack justify="space-between" align="center" wrap="wrap" gap={4}>
                                            <Text fontSize="sm" color="gray.700">
                                              <strong>{t('dashboard.id')}:</strong> {review.id}
                                            </Text>
                                            <Text fontSize="sm" color="gray.700">
                                              <strong>{t('dashboard.created')}:</strong> {formatCompactDateTime(review.created_at)}
                                            </Text>
                                            {review.completed_at && (
                                              <Text fontSize="sm" color="gray.700">
                                                <strong>{t('dashboard.completed')}:</strong> {formatCompactDateTime(review.completed_at)}
                                              </Text>
                                            )}
                                            {review.status === 'completed' && review.compliance_score !== null && review.compliance_score !== undefined && (
                                              <Text fontSize="sm" color="gray.700">
                                                <strong>{t('dashboard.level')}:</strong> {getComplianceLevel(review.compliance_score)}
                                              </Text>
                                            )}
                                          </HStack>
                                        </VStack>
                                      </Box>
                                    </Collapsible.Content>
                                  </Collapsible.Root>
                                </Table.Cell>
                              </Table.Row>
                            </Fragment>
                          );
                        })}
                      </Table.Body>
                    </Table.Root>
                  </Box>
                ) : (
                  <VStack gap={4} py={8}>
                    <Box 
                      p={4} 
                      rounded="full" 
                      bg="gray.100"
                      color="gray.400"
                    >
                      <Icon as={FiFileText} boxSize={8} />
                    </Box>
                    <VStack gap={2}>
                      <Text color="gray.500" textAlign="center" fontWeight="medium">
                        {t('dashboard.noRecentAssessments')}
                      </Text>
                      <Text fontSize="sm" color="gray.400" textAlign="center">
                        {t('dashboard.startFirstAssessment')}
                      </Text>
                      <RouterLink to="/create-review" style={{ textDecoration: 'none' }}>
                        <Button
                          size="sm"
                          colorPalette="blue"
                          variant="outline"
                          borderRadius="full"
                        >
                          <FiPlus />
                          {t('dashboard.startAssessment')}
                        </Button>
                      </RouterLink>
                    </VStack>
                  </VStack>
                )}
              </VStack>
            </Card.Body>
          </Card.Root>
        </VStack>
      </Container>
    </Box>
  );
};

export default Dashboard;
